import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { CornerAccents } from '@/components/CornerAccents';
import { Reveal } from '@/components/motion/Reveal';
import { SpotlightCard } from '@/components/motion/SpotlightCard';

const HEADING_FONT = { fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" };
const BODY_FONT = { fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" };

const PRODUCTS = [
  {
    id: 'vowpace',
    name: 'Vowpace AI',
    accent: '#6366F1',
    image: '/press-kit/vowpace-og.jpg',
    imgClass: 'object-left',
    icon: '/press-kit/vowpace-icon.png',
    overview: '/products/vowpace-ai',
    launch: 'https://vowpace.com',
    tags: ['B2B Enterprise', 'Construction AI'],
    taglineKey: 'products.vw_tagline',
    descKey: 'products.vw_desc',
    specs: ['US SUBCONTRACTS', 'LD', 'INDEMNITY', 'PAY-WHEN-PAID'],
  },
  {
    id: 'lcshield',
    name: 'LC Shield',
    accent: '#F59E0B',
    image: '/press-kit/lcshield-og.png',
    imgClass: '',
    logo: '/press-kit/lcshield-logo.png',
    overview: '/products/lc-shield',
    launch: 'https://getlcshield.com',
    tags: ['B2B FinTech', 'International Trade'],
    taglineKey: 'products.lc_tagline',
    descKey: 'products.lc_desc',
    specs: ['UCP 600', 'ISBP 821 (2023)', 'MT700', 'eUCP 2.1'],
  },
];

function TabBar({ active, setActive }) {
  return (
    <div data-testid="product-tab-bar" className="inline-flex p-1.5 rounded-full bg-[#0E131F] border border-slate-800/60 mb-12">
      {PRODUCTS.map((p) => (
        <button
          key={p.id}
          data-testid={`product-tab-${p.id}`}
          onClick={() => setActive(p.id)}
          className={`relative px-6 md:px-9 py-2.5 rounded-full text-sm font-bold transition-colors duration-300 ${
            active === p.id ? 'text-white' : 'text-[#94A3B8] hover:text-white'
          }`}
          style={HEADING_FONT}
        >
          {active === p.id && (
            <motion.span
              layoutId="product-tab-pill"
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: p.accent, boxShadow: `0 4px 24px ${p.accent}50` }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            />
          )}
          <span className="relative z-10">{p.name}</span>
        </button>
      ))}
    </div>
  );
}

function ProductPanel({ p }) {
  const { t } = useLanguage();

  return (
    <motion.div
      key={p.id}
      data-testid={`product-panel-${p.id}`}
      initial={{ opacity: 0, x: 48 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -48 }}
      transition={{ duration: 0.45, ease: [0.21, 0.47, 0.32, 0.98] }}
      className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
    >
      <div className="lg:col-span-7">
        <SpotlightCard glow={p.accent} className="rounded-2xl">
          <div className="relative rounded-2xl overflow-hidden border border-slate-800/60 bg-[#0A0E16] shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
            <CornerAccents color={p.accent} />
            <div className="h-8 flex items-center gap-1.5 px-4 bg-[#0E131F] border-b border-slate-800/60">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#64748B]">
                {new URL(p.launch).hostname}
              </span>
            </div>
            <img src={p.image} alt={`${p.name} preview`} className={`w-full object-cover ${p.imgClass}`} />
          </div>
        </SpotlightCard>
      </div>
      <div className="lg:col-span-5">
        <div className="flex flex-wrap gap-2 mb-5">
          {p.tags.map((x) => (
            <span
              key={x}
              className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
              style={{ color: p.accent, backgroundColor: `${p.accent}14`, border: `1px solid ${p.accent}35` }}
            >
              {x}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3 mb-2">
          {p.icon && <img src={p.icon} alt="" className="w-9 h-9 rounded-lg object-cover" />}
          {p.logo && <img src={p.logo} alt="" className="w-9 h-auto" style={{ filter: 'drop-shadow(0 0 8px rgba(129,140,248,0.6))' }} />}
          <h2 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight" style={HEADING_FONT}>{p.name}</h2>
        </div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-4" style={BODY_FONT}>{t(p.taglineKey)}</p>
        <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed mb-6" style={BODY_FONT}>{t(p.descKey)}</p>
        <div className="flex flex-wrap gap-2 mb-8">
          {p.specs.map((spec) => (
            <span
              key={spec}
              className="px-3 py-1.5 rounded font-mono text-[10px] font-bold tracking-widest text-[#94A3B8] bg-white/[0.03] border border-slate-800/60 hover:-translate-y-0.5 transition-transform duration-200 cursor-default"
            >
              {spec}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to={p.overview}>
            <Button
              data-testid={`product-cta-${p.id}`}
              className="h-12 px-7 rounded-full text-sm font-bold transition-all text-white hover:brightness-110"
              style={{ backgroundColor: p.accent, boxShadow: `0 8px 28px ${p.accent}40` }}
            >
              {t('products.learn_more')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <a href={p.launch} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid={`product-launch-${p.id}`}
              variant="outline"
              className="h-12 px-7 rounded-full bg-transparent border-white/20 text-[#F8FAFC] text-sm font-semibold hover:bg-white/10 hover:text-white transition-all"
            >
              {t('products.launch_app')}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function ProductsPage() {
  const { t } = useLanguage();
  const [active, setActive] = useState('vowpace');
  const current = PRODUCTS.find((p) => p.id === active);

  return (
    <div data-testid="products-page" className="pt-20 bg-[#080B10]">
      <SEOHead path="/products" />
      <section className="py-24 md:py-28 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(640px circle at 50% 0%, ${current.accent}12, transparent 60%)` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-10">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8] mb-4">PRODUCTS</p>
            <h1
              data-testid="products-title"
              className="text-4xl md:text-5xl lg:text-6xl tracking-tighter font-bold text-[#F8FAFC] leading-tight"
              style={HEADING_FONT}
            >
              {t('products.title')}
            </h1>
            <p className="mt-4 text-base md:text-lg text-[#94A3B8] max-w-xl mx-auto" style={BODY_FONT}>
              {t('products.subtitle')}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="text-center">
            <TabBar active={active} setActive={setActive} />
          </Reveal>
          <AnimatePresence mode="wait" initial={false}>
            <ProductPanel key={active} p={current} />
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
